import { useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import useLatest from "@-/com.npmjs.use-latest";
import {
  sentenceDrillCardSetQueryFn,
  useGetReviewCardFsrsResolutionQueryKey,
  useGetSentenceDrillCardSetQueryKey,
} from "@-/dev.improve.haniverse.lib.cards.study-card-data";
import { reviewCardFsrsResolutionQueryFn } from "@-/dev.improve.haniverse.lib.cards.study-card-data/use-review-card-fsrs-resolution-query";
import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import type { LearnCardActionSnapshot } from "@-/dev.improve.haniverse.lib.learn.card-action";
import type { SentenceDrillState } from "@-/dev.improve.haniverse.lib.sentence.drill";
import { useCallback } from "react";

/**
 * Freezes a REVIEW card into an action snapshot.
 *
 * The FSRS resolution is fetched fresh rather than read from a render-time value: the front never
 * had one of its own, a swipe may start before the prefetch settled, and the resolution only
 * identifies WHICH card to advance (the schedule itself is computed inside the write). A `null`
 * resolution is a legitimate snapshot — the rating-less "Continue" card.
 */
export function useResolveReviewCardSnapshot(): (input: {
  drillType: DrillType;
  grapheme: string;
}) => Promise<Extract<LearnCardActionSnapshot, { kind: "review" }>> {
  const queryClient = useQueryClient();
  const getReviewCardFsrsResolutionQueryKeyRef = useLatest(
    useGetReviewCardFsrsResolutionQueryKey(),
  );

  return useCallback(
    async ({ drillType, grapheme }) => {
      const fsrsResolution = await queryClient.fetchQuery({
        queryFn: reviewCardFsrsResolutionQueryFn,
        queryKey: getReviewCardFsrsResolutionQueryKeyRef.current(grapheme, drillType),
      });
      return {
        card:
          fsrsResolution === null
            ? null
            : { fsrsCardId: fsrsResolution.fsrsCard.id, studyCardId: fsrsResolution.studyCard.id },
        drillType,
        grapheme,
        kind: "review",
      };
    },
    [getReviewCardFsrsResolutionQueryKeyRef, queryClient],
  );
}

/**
 * Freezes a SENTENCE-PRODUCTION card into an action snapshot: the drill state as it stands at the
 * commit, plus the in-study card behind each word. Words the learner never added have no card and
 * are simply absent from the map — rating them would enrol them in vocabulary they never chose.
 */
export function useResolveSentenceCardSnapshot(): (input: {
  drillType: DrillType;
  state: SentenceDrillState;
  word: string;
}) => Promise<Extract<LearnCardActionSnapshot, { kind: "sentence" }>> {
  const queryClient = useQueryClient();
  const getSentenceDrillCardSetQueryKeyRef = useLatest(useGetSentenceDrillCardSetQueryKey());

  return useCallback(
    async ({ drillType, state, word }) => {
      const cardByTokenIndex = await queryClient.fetchQuery({
        queryFn: sentenceDrillCardSetQueryFn,
        queryKey: getSentenceDrillCardSetQueryKeyRef.current(
          state.tokens.map((token) => token.word),
          drillType.kind,
        ),
      });
      const cards = new Map<number, { fsrsCardId: string; studyCardId: string }>();
      for (const [tokenIndex, card] of cardByTokenIndex) {
        cards.set(tokenIndex, { fsrsCardId: card.fsrsCardId, studyCardId: card.studyCardId });
      }
      return { cards, drillType, kind: "sentence", state, word };
    },
    [getSentenceDrillCardSetQueryKeyRef, queryClient],
  );
}
